/**
 * Chart component for canvas rendering.
 * Data visualization using recharts (line, bar, pie, area).
 */

import React, { memo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { ChartProps as ChartPropsType } from '../../../types/canvas';
import { useIsDarkMode } from '../../../hooks/useIsDarkMode';

export interface ChartComponentProps extends ChartPropsType {}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

function ChartComponentBase({
  type,
  data,
  xKey,
  yKey,
  title,
  xLabel,
  yLabel,
  colors = DEFAULT_COLORS,
}: ChartComponentProps) {
  const chartColors = colors.length > 0 ? colors : DEFAULT_COLORS;
  const isDark = useIsDarkMode();
  // Recharts takes literal color strings, so it can't use Tailwind dark:
  // variants — pick palette based on the live <html>.dark class.
  const gridStroke = isDark ? '#374151' : '#e5e7eb'; // gray-700 / gray-200
  const tooltipBg = isDark ? '#1f2937' : '#ffffff'; // gray-800 / white
  const tooltipText = isDark ? '#f3f4f6' : '#111827'; // gray-100 / gray-900
  const tooltipStyle = {
    backgroundColor: tooltipBg,
    border: `1px solid ${gridStroke}`,
    borderRadius: '8px',
    fontSize: '12px',
    color: tooltipText,
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridStroke }}
              label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridStroke }}
              label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line
              type="monotone"
              dataKey={yKey}
              stroke={chartColors[0]}
              strokeWidth={2}
              dot={{ fill: chartColors[0], strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridStroke }}
              label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridStroke }}
              label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar dataKey={yKey} fill={chartColors[0]} radius={[4, 4, 0, 0]} />
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridStroke }}
              label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: gridStroke }}
              label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Area
              type="monotone"
              dataKey={yKey}
              stroke={chartColors[0]}
              fill={chartColors[0]}
              fillOpacity={0.3}
            />
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={yKey}
              nameKey={xKey}
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              labelLine={true}
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
          </PieChart>
        );

      default:
        return <div className="text-gray-500 dark:text-gray-400 text-sm">Unknown chart type: {type}</div>;
    }
  };

  return (
    <div className="w-full">
      {title && <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">{title}</h4>}
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer>{renderChart()}</ResponsiveContainer>
      </div>
    </div>
  );
}

export const ChartComponent = memo(ChartComponentBase);
